import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { DocumentSummaryCachedQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

/** How often a `running` status is re-read while waiting for the subscription's terminal event. */
export const SUMMARY_RUNNING_POLL_MS = 30_000;

export const documentSummaryCachedQueryKey = (documentId: string): QueryKey => [
  "documentSummaryCached",
  documentId,
];

const documentSummaryCachedQuery = graphql(`
  query documentSummaryCached($documentId: String!) {
    documentSummaryCached(documentId: $documentId) {
      operationId
      status
      result {
        summary
        sources {
          type
          id
          title
          documentType
          chunkNumber
          citationId
          filePath
          isDeleted
          locations {
            pageNumber
            bbox {
              x
              y
              width
              height
            }
          }
        }
      }
    }
  }
`);

export type DocumentSummaryCachedResponse = DocumentSummaryCachedQuery["documentSummaryCached"];
export type DocumentSummaryResult = NonNullable<DocumentSummaryCachedResponse["result"]>;

export function useDocumentSummaryCached(
  documentId: string,
  options?: UseQueryOptions<
    DocumentSummaryCachedQuery,
    unknown,
    DocumentSummaryCachedQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    documentSummaryCachedQueryKey(documentId),
    documentSummaryCachedQuery,
    [{ documentId }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      // A failed run is only reported until its cooldown ends, so re-read it on mount rather
      // than serving the failure from cache for the rest of the session.
      refetchOnMount: (query) =>
        query.state.data?.documentSummaryCached.status === "failed" ? "always" : false,
      // The subscription is the fast path; polling recovers a terminal event that never arrived.
      refetchInterval: (data) =>
        data?.documentSummaryCached.status === "running" ? SUMMARY_RUNNING_POLL_MS : false,
      ...options,
    },
  );

  return { data: data?.documentSummaryCached, ...rest };
}
